import type { MarketingLocale } from "./marketing-copy";

const sharedRows = {
  en: [
    ["AI-generated CV conforming to Canadian standards", "AI reorganizes your CV", "AI reorganizes your CV"],
    ["Manual editing", "Unlimited", "Unlimited for 7 days"],
    ["Visual overview of the CV", "And immediately (with watermark CVBUILDER)", "PDF HD proper without watermark"],
    ["AI regenerations", "1 free regeneration", "Up to 5 regenerations included"],
    ["NOC Code and Title", "Masked (NOC 00000)", "Accessible professional code and title"],
    ["AI translation", "Locked option", "1 complete translation offered"],
    ["Bilingual CV Pack", "Not included", "1 French CV + 1 English CV included"],
    ["PDF Download", "Unlocked upon payment", "Instant & unlimited download"],
    ["Duration of access", "Access to the current session", "Access & modifications for 7 days"],
  ],
  fr: [
    ["CV généré par l’IA selon les normes canadiennes", "L’IA réorganise votre CV", "L’IA réorganise votre CV"],
    ["Modification manuelle", "Illimitée", "Illimitée pendant 7 jours"],
    ["Aperçu visuel du CV", "Disponible avec filigrane CVBuilder", "PDF HD propre sans filigrane"],
    ["Régénérations par l’IA", "1 régénération gratuite", "Jusqu’à 5 régénérations incluses"],
    ["Code et titre CNP", "Masqués (CNP 00000)", "Code et titre professionnels officiels accessibles"],
    ["Traduction par l’IA", "Verrouillée", "1 traduction complète incluse"],
    ["Ensemble de CV bilingue", "Non inclus", "1 CV français + 1 CV anglais inclus"],
    ["Téléchargement PDF", "Débloqué avec le paiement", "Téléchargements instantanés et illimités"],
    ["Durée d’accès", "Session en cours", "Accès et modifications pendant 7 jours"],
  ],
} as const;

export const pricingCopy = {
  en: {
    badge: "7-day access · no subscription",
    title: "Give your resume every chance to stand out to Canadian employers",
    description: "Get an AI-generated French and English resume, your official National Occupational Classification (NOC) title and code, and a professional HD PDF without a watermark — all in minutes.",
    create: "Create my CV",
    compareAction: "Compare plans",
    compareTitle: "Free version vs. Full access",
    columns: ["Functionality", "Free version", "Full access (7 consecutive days)"],
    rows: sharedRows.en,
    includedEyebrow: "Included with full access",
    includedTitle: "Everything you need to finalize your CV",
    benefits: [
      ["Bilingual resume pack included", "1 French version + 1 AI-generated English version."],
      ["Job title and NOC code available.", "Access the NOC occupational codes and titles associated with your profile."],
      ["Up to 5 AI regenerations", "Improve your content and generate different versions of your CV."],
      ["Unlimited manual revisions", "Update your CV as often as necessary during your access period."],
      ["Professional HD PDF", "Download a clean, high-resolution document without any watermarks."],
      ["Unlimited downloads for 7 days", "Download your finalized CV at any time within a 7-day period."],
    ],
    pricingTitle: "Finalize your resume today",
    access: "Full access — 7 days",
    price: "$19.99",
    taxNote: "CAD",
    duration: "Full access for 7 days",
    bilingual: "1 French resume + 1 English resume included",
    signInNote: "Sign in to get started. Activate full access whenever you want.",
    note: "No long-term subscription. Your full access is available for 7 days.",
    trust: ["Secure payment via Stripe", "Designed for the Canadian job market", "Instant HD PDF download", "French and English resumes included"],
  },
  fr: {
    badge: "Accès de 7 jours · sans abonnement",
    title: "Donnez à votre CV toutes les chances de se démarquer auprès des employeurs canadiens",
    description: "Obtenez un CV généré par l’IA en français et en anglais, votre titre et votre code officiels de la Classification nationale des professions (CNP), ainsi qu’un PDF HD professionnel sans filigrane — en quelques minutes.",
    create: "Créer mon CV",
    compareAction: "Comparer les offres",
    compareTitle: "Version gratuite ou accès complet",
    columns: ["Fonctionnalité", "Version gratuite", "Accès complet (7 jours consécutifs)"],
    rows: sharedRows.fr,
    includedEyebrow: "Inclus avec l’accès complet",
    includedTitle: "Tout ce qu’il vous faut pour finaliser votre CV",
    benefits: [
      ["Ensemble de CV bilingue inclus", "1 version française + 1 version anglaise générée par l’IA."],
      ["Titre d’emploi et code CNP accessibles", "Accédez au code et au titre CNP officiels associés à votre profil."],
      ["Jusqu’à 5 régénérations par l’IA", "Améliorez votre contenu et créez différentes versions de votre CV."],
      ["Révisions manuelles illimitées", "Modifiez votre CV aussi souvent que nécessaire pendant votre période d’accès."],
      ["PDF HD professionnel", "Téléchargez un document propre et haute résolution, sans filigrane."],
      ["Téléchargements illimités pendant 7 jours", "Téléchargez votre CV finalisé à tout moment pendant 7 jours."],
    ],
    pricingTitle: "Finalisez votre CV aujourd’hui",
    access: "Accès complet — 7 jours",
    price: "19,99 $",
    taxNote: "CAD",
    duration: "Accès complet pendant 7 jours",
    bilingual: "1 CV français + 1 CV anglais inclus",
    signInNote: "Connectez-vous pour commencer. Activez l’accès complet quand vous le souhaitez.",
    note: "Aucun abonnement à long terme. Votre accès complet est disponible pendant 7 jours.",
    trust: ["Paiement sécurisé par Stripe", "Conçu pour le marché du travail canadien", "Téléchargement PDF HD instantané", "CV français et anglais inclus"],
  },
} as const satisfies Record<MarketingLocale, object>;
